import { Product } from '@/types';
import { auth } from '@/lib/firebase';
import { Auth } from 'firebase/auth';

/**
 * Get authorization header with ID token
 */
async function getAuthHeader(): Promise<string> {
  if (!auth) {
    throw new Error('Auth not initialized');
  }
  const user = (auth as Auth).currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  const idToken = await user.getIdToken();
  return `Bearer ${idToken}`;
}

/**
 * Get all products (for admin) - via API
 */
export async function getAllProductsAdmin(): Promise<Product[]> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/products`, {
      headers: {
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch products');
    }

    const data = await response.json();
    return data.products || [];
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

/**
 * Get a single product by ID - via API
 */
export async function getProductById(productId: string): Promise<Product | null> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/products/${productId}`, {
      headers: {
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch product');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
}

/**
 * Create a new product - via API
 */
export async function createProduct(
  productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/products`, {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create product');
    }

    const data = await response.json();
    return data.id;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
}

/**
 * Update an existing product - via API
 */
export async function updateProduct(
  productId: string,
  productData: Partial<Omit<Product, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<void> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/products/${productId}`, {
      method: 'PUT',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update product');
    }
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
}

/**
 * Delete a product - via API
 */
export async function deleteProduct(productId: string): Promise<void> {
  try {
    const authHeader = await getAuthHeader();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
    const response = await fetch(`${apiBaseUrl}/api/admin/products/${productId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': authHeader,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete product');
    }
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
}

/**
 * Generate slug from product name
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Check if slug is unique - via API
 */
export async function isSlugUnique(slug: string, excludeId?: string): Promise<boolean> {
  try {
    const products = await getAllProductsAdmin();
    return products.every((product) => {
      if (excludeId && product.id === excludeId) {
        return true;
      }
      return product.slug !== slug;
    });
  } catch (error) {
    console.error('Error checking slug uniqueness:', error);
    return false;
  }
}

